import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { progressAPI, skillProgressAPI } from '../api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Sparkles, BookOpen, Trophy, Flame, Star, Target,
  Keyboard, Puzzle, Award, Calendar, TrendingUp, Zap, Heart,
  Compass, Palette, Brain, Leaf, Shield, Footprints, GraduationCap,
  Lightbulb, BookOpenCheck
} from 'lucide-react';

// Rank icon mapper
const RANK_ICONS = {
  sparkle: Sparkles,
  compass: Compass,
  footprints: Footprints,
  star: Star,
  shield: Shield
};

// Realm display data
const REALM_INFO = {
  memory_garden: { name: 'Memory Garden', color: '#4ADE80', icon: Leaf },
  radiant_will: { name: 'Radiant Will', color: '#FBBF24', icon: Heart },
  star_map: { name: 'Star Map Hall', color: '#60A5FA', icon: Compass },
  creative_forge: { name: 'Creative Forge', color: '#FB923C', icon: Palette },
  calm_cove: { name: 'Calm Cove', color: '#2DD4BF', icon: Brain }
};

// Spark type info
const SPARK_INFO = {
  knowledge: { name: 'Knowledge', color: '#4ADE80', icon: BookOpen },
  creativity: { name: 'Creativity', color: '#FB923C', icon: Palette },
  discovery: { name: 'Discovery', color: '#60A5FA', icon: Compass },
  calm: { name: 'Calm', color: '#2DD4BF', icon: Brain },
  willpower: { name: 'Willpower', color: '#FBBF24', icon: Heart }
};

// Badge icon mapper
const BADGE_ICONS = {
  star: Star,
  book: BookOpen,
  lotus: Brain,
  palette: Palette,
  heart: Heart,
  trophy: Trophy,
  sparkles: Sparkles,
  road: Target,
  keyboard: Keyboard,
  compass: Compass
};

const ChildProgressPage = () => {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [progress, setProgress] = useState(null);
  const [rank, setRank] = useState(null);
  const [skillProgress, setSkillProgress] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (childId) {
      fetchProgress();
    }
  }, [childId]);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      const [progressRes, rankRes, skillRes, recsRes] = await Promise.all([
        progressAPI.getFullProgress(childId),
        progressAPI.getRank(childId).catch(() => ({ data: null })),
        skillProgressAPI.getProgress(childId).catch(() => ({ data: null })),
        skillProgressAPI.getRecommendations(childId, 3).catch(() => ({ data: null }))
      ]);
      setProgress(progressRes.data);
      setRank(rankRes.data);
      setSkillProgress(skillRes.data);
      setRecommendations(recsRes.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
      toast.error('Failed to load progress data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white text-xl font-philosopher animate-pulse">
          Loading progress...
        </div>
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-400 mb-4">Could not load progress data</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-indigo-400 hover:text-indigo-300"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-6 h-6 text-slate-400" />
            </button>
            <div>
              <h1 className="text-3xl font-philosopher font-bold text-white">
                {progress.child_name}'s Progress
              </h1>
              <p className="text-slate-400">
                Life Path {progress.life_path || 'Not set'} • Learning Journey Overview
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Total Sparks */}
          <div className="glass-panel p-4 rounded-xl" data-testid="total-sparks-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Sparks</p>
                <p className="text-2xl font-bold text-white">{progress.total_sparks}</p>
              </div>
            </div>
          </div>

          {/* Lessons Completed */}
          <div className="glass-panel p-4 rounded-xl" data-testid="lessons-completed-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Lessons Done</p>
                <p className="text-2xl font-bold text-white">{progress.total_lessons_completed}</p>
              </div>
            </div>
          </div>

          {/* Current Streak */}
          <div className="glass-panel p-4 rounded-xl" data-testid="streak-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Flame className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Day Streak</p>
                <p className="text-2xl font-bold text-white">{progress.current_streak}</p>
              </div>
            </div>
          </div>

          {/* Badges Earned */}
          <div className="glass-panel p-4 rounded-xl" data-testid="badges-earned-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Badges</p>
                <p className="text-2xl font-bold text-white">{progress.badges_earned}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Temple Rank Card */}
        {rank?.current_rank && (
          <div className="glass-panel p-6 rounded-2xl mb-8" data-testid="rank-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${rank.current_rank.color}20` }}
                >
                  {(() => {
                    const RankIcon = RANK_ICONS[rank.current_rank.icon] || Sparkles;
                    return <RankIcon className="w-7 h-7" style={{ color: rank.current_rank.color }} />;
                  })()}
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Temple Rank</p>
                  <p className="text-2xl font-bold" style={{ color: rank.current_rank.color }}>
                    {rank.current_rank.name}
                  </p>
                </div>
              </div>
              {rank.next_rank && (
                <div className="text-right">
                  <p className="text-slate-400 text-sm">Next rank: {rank.next_rank.name}</p>
                  <p className="text-sm" style={{ color: rank.next_rank.color }}>
                    {rank.sparks_to_next} sparks to go
                  </p>
                  <div className="mt-2 w-32 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${Math.min(100, ((rank.total_sparks - rank.current_rank.min_sparks) / (rank.next_rank.min_sparks - rank.current_rank.min_sparks)) * 100)}%`,
                        backgroundColor: rank.current_rank.color 
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Spark Breakdown */}
            <div className="glass-panel p-6 rounded-2xl" data-testid="spark-breakdown">
              <h2 className="text-xl font-philosopher font-bold text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                Spark Collection
              </h2>
              <div className="space-y-3">
                {Object.entries(progress.sparks).map(([type, amount]) => {
                  const info = SPARK_INFO[type];
                  const IconComponent = info?.icon || Sparkles;
                  const maxSparks = Math.max(...Object.values(progress.sparks), 1);
                  const percentage = (amount / maxSparks) * 100;
                  
                  return (
                    <div key={type} className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${info?.color}20` }}
                      >
                        <IconComponent className="w-4 h-4" style={{ color: info?.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-slate-300">{info?.name || type}</span>
                          <span className="text-sm font-semibold text-white">{amount}</span>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-500"
                            style={{ 
                              width: `${percentage}%`, 
                              backgroundColor: info?.color 
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Progress by Realm */}
            <div className="glass-panel p-6 rounded-2xl" data-testid="realm-progress">
              <h2 className="text-xl font-philosopher font-bold text-white mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-400" />
                Progress by Realm
              </h2>
              <div className="space-y-3">
                {Object.entries(REALM_INFO).map(([realmId, info]) => {
                  const lessonsCompleted = progress.lessons_by_realm[realmId] || 0;
                  const IconComponent = info.icon;
                  
                  return (
                    <div key={realmId} className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${info.color}20` }}
                      >
                        <IconComponent className="w-4 h-4" style={{ color: info.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-300">{info.name}</span>
                          <span className="text-sm font-semibold text-white">
                            {lessonsCompleted} lessons
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Activity Stats */}
            <div className="glass-panel p-6 rounded-2xl" data-testid="activity-stats">
              <h2 className="text-xl font-philosopher font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-cyan-400" />
                Activity Stats
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-900/50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Keyboard className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm text-slate-400">Typing Dojo</span>
                  </div>
                  <p className="text-lg font-semibold text-white">Level {progress.typing_level}</p>
                  <p className="text-xs text-slate-500">
                    Best: {progress.typing_best_wpm} WPM
                  </p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Puzzle className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-slate-400">Courtyard</span>
                  </div>
                  <p className="text-lg font-semibold text-white">
                    {progress.courtyard_puzzles_solved} Puzzles
                  </p>
                  <p className="text-xs text-slate-500">
                    {progress.constellations_completed} Constellations
                  </p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm text-slate-400">Quests</span>
                  </div>
                  <p className="text-lg font-semibold text-white">
                    {progress.total_quests_completed} Total
                  </p>
                  <p className="text-xs text-slate-500">
                    {progress.quests_today} today
                  </p>
                </div>
                <div className="bg-slate-900/50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-slate-400">Best Streak</span>
                  </div>
                  <p className="text-lg font-semibold text-white">
                    {progress.longest_streak} days
                  </p>
                  <p className="text-xs text-slate-500">
                    Current: {progress.current_streak}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Learning Paths */}
            <div className="glass-panel p-6 rounded-2xl" data-testid="learning-paths">
              <h2 className="text-xl font-philosopher font-bold text-white mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-400" />
                Learning Paths
              </h2>
              <div className="flex items-center gap-4 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">{progress.paths_completed}</p>
                  <p className="text-xs text-slate-400">Completed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-400">{progress.paths_in_progress}</p>
                  <p className="text-xs text-slate-400">In Progress</p>
                </div>
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {progress.path_details.map((path) => {
                  const realmInfo = REALM_INFO[path.room_id] || {};
                  
                  return (
                    <div 
                      key={path.id} 
                      className="bg-slate-900/50 p-3 rounded-xl"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-sm font-semibold text-white">{path.title}</p>
                          <p className="text-xs text-slate-500">
                            {path.completed_lessons}/{path.total_lessons} lessons
                          </p>
                        </div>
                        {path.is_completed && (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                            Complete!
                          </span>
                        )}
                      </div>
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${path.progress_percent}%`, 
                            backgroundColor: path.is_completed ? '#4ADE80' : realmInfo.color || '#8B5CF6'
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
                {progress.path_details.length === 0 && (
                  <p className="text-slate-500 text-sm text-center py-4">
                    No learning paths started yet
                  </p>
                )}
              </div>
            </div>

            {/* Badges */}
            <div className="glass-panel p-6 rounded-2xl" data-testid="badges-display">
              <h2 className="text-xl font-philosopher font-bold text-white mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-400" />
                Badges Earned
              </h2>
              {progress.badges.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {progress.badges.map((badge, index) => {
                    const IconComponent = BADGE_ICONS[badge.icon] || Star;
                    
                    return (
                      <div 
                        key={index}
                        className="bg-slate-900/50 p-3 rounded-xl flex items-center gap-3"
                      >
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${badge.color}30` }}
                        >
                          <IconComponent 
                            className="w-5 h-5" 
                            style={{ color: badge.color }}
                          />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">{badge.name}</p>
                          <p className="text-xs text-slate-500 line-clamp-1">{badge.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Award className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No badges earned yet</p>
                  <p className="text-slate-600 text-xs mt-1">Complete lessons to earn badges!</p>
                </div>
              )}
            </div>

            {/* Skill Progress & Recommendations */}
            {recommendations && (
              <div className="glass-panel p-6 rounded-2xl" data-testid="skill-recommendations">
                <h2 className="text-xl font-philosopher font-bold text-white mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-cyan-400" />
                  Learning Intelligence
                </h2>
                
                {/* Child's Learning Profile */}
                <div className="bg-slate-900/50 p-3 rounded-xl mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Learning Profile</span>
                    <span className="text-sm text-white">
                      Age {recommendations.child_age} • Grade {recommendations.child_grade}
                    </span>
                  </div>
                </div>

                {/* Active Skills */}
                {Object.keys(recommendations.skill_summary || {}).length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Active Skills</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(recommendations.skill_summary)
                        .filter(([_, data]) => data.total_completed > 0)
                        .slice(0, 6)
                        .map(([skill, data]) => {
                          const masteryColors = {
                            novice: '#94A3B8',
                            apprentice: '#60A5FA',
                            journeyman: '#4ADE80',
                            expert: '#FBBF24',
                            master: '#A78BFA'
                          };
                          return (
                            <span 
                              key={skill}
                              className="px-2 py-1 rounded-full text-xs font-medium"
                              style={{ 
                                backgroundColor: `${masteryColors[data.mastery_level]}20`,
                                color: masteryColors[data.mastery_level]
                              }}
                            >
                              {skill} • {data.mastery_level}
                            </span>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Recommended Next Lessons */}
                {recommendations.next_lessons?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                      <Lightbulb className="w-3 h-3" /> Recommended Next
                    </p>
                    <div className="space-y-2">
                      {recommendations.next_lessons.slice(0, 2).map((lesson, idx) => (
                        <div key={idx} className="bg-slate-900/50 p-2 rounded-lg flex items-center gap-2">
                          <BookOpenCheck className="w-4 h-4 text-green-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{lesson.lesson_title}</p>
                            <p className="text-xs text-slate-500">{lesson.difficulty_level}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Support Needed */}
                {recommendations.struggling_skills?.length > 0 && (
                  <div className="p-3 rounded-xl bg-orange-500/10 border border-orange-500/20">
                    <p className="text-xs text-orange-400 font-medium mb-1">Areas to Practice</p>
                    <div className="flex flex-wrap gap-1">
                      {recommendations.struggling_skills.map(skill => (
                        <span key={skill} className="px-2 py-0.5 bg-orange-500/20 text-orange-300 text-xs rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mastered Skills */}
                {recommendations.mastered_skills?.length > 0 && (
                  <div className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/20 mt-3">
                    <p className="text-xs text-purple-400 font-medium mb-1">Mastered Skills</p>
                    <div className="flex flex-wrap gap-1">
                      {recommendations.mastered_skills.map(skill => (
                        <span key={skill} className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recent Activity */}
            <div className="glass-panel p-6 rounded-2xl" data-testid="recent-activity">
              <h2 className="text-xl font-philosopher font-bold text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-400" />
                Recent Lessons
              </h2>
              {progress.recent_lessons.length > 0 ? (
                <div className="space-y-2">
                  {progress.recent_lessons.map((lesson, index) => {
                    const realmInfo = REALM_INFO[lesson.room_id] || {};
                    const completedDate = lesson.completed_at 
                      ? new Date(lesson.completed_at).toLocaleDateString()
                      : 'Recently';
                    
                    return (
                      <div 
                        key={index}
                        className="flex items-center gap-3 bg-slate-900/50 p-3 rounded-xl"
                      >
                        <div 
                          className="w-2 h-8 rounded-full"
                          style={{ backgroundColor: realmInfo.color || '#8B5CF6' }}
                        />
                        <div className="flex-1">
                          <p className="text-sm text-white">{lesson.title}</p>
                          <p className="text-xs text-slate-500">{realmInfo.name || lesson.room_id}</p>
                        </div>
                        <span className="text-xs text-slate-500">{completedDate}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-500 text-sm text-center py-4">
                  No lessons completed yet
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Streak Milestones Banner */}
        {progress.current_streak > 0 && (
          <div className="mt-8 glass-panel p-6 rounded-2xl bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/30">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                <Flame className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">
                  {progress.current_streak} Day Streak!
                </h3>
                <p className="text-slate-400 text-sm">
                  {progress.current_streak < 3 
                    ? `Keep going! ${3 - progress.current_streak} more days to unlock the 3-day bonus!`
                    : progress.current_streak < 7
                    ? `Amazing! ${7 - progress.current_streak} more days to the 7-day milestone!`
                    : progress.current_streak < 14
                    ? `Incredible! ${14 - progress.current_streak} more days to the 14-day achievement!`
                    : "You're a learning legend! Keep the flame burning!"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-400">Best streak</p>
                <p className="text-2xl font-bold text-white">{progress.longest_streak} days</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChildProgressPage;
